import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { selectV2 } from './v2Upload.slice';
import tourForStep from './utils/tourSteps';

const SEEN_KEY = 'ff.v2tour.seen';

/** Outside the 0..5 step range, so group mode shares the seen-list safely. */
const INTERSTITIAL_TOUR_KEY = -2;
const GROUP_REVIEW_TOUR_KEY = -3;

function seenSteps(): number[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
    return Array.isArray(raw) ? (raw as number[]) : [];
  } catch {
    return [];
  }
}

function markSeen(step: number): void {
  try {
    const next = [...new Set([...seenSteps(), step])];
    localStorage.setItem(SEEN_KEY, JSON.stringify(next));
  } catch {
    // Best-effort
  }
}

const stageTourKey = (stage?: 'interstitial' | 'review') => {
  if (stage === 'interstitial') return INTERSTITIAL_TOUR_KEY;
  if (stage === 'review') return GROUP_REVIEW_TOUR_KEY;
  return undefined;
};

/** Outlasts the step's enter animation; anchoring mid-flight misplaces the popover. */
const SETTLE_MS = 350;

const token = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** The wizard's furniture is worth showing once and skippable every time — the
 * user who came to fill in a form does not owe it four more clicks. */
function addSkipButton(footerButtons: HTMLElement, onSkip: () => void): void {
  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'driver-popover-footer-btn driver-popover-skip-btn';
  skip.textContent = 'Skip';
  skip.addEventListener('click', onSkip);
  footerButtons.prepend(skip);
}

// fallow-ignore-next-line complexity
export default function useTour(): () => void {
  const { step, focusField, mode, multiStage } = useSelector(selectV2);
  const multi = mode === 'multi';
  const stage = multi && multiStage !== 'workpiece' ? multiStage : undefined;
  const tourKey = stageTourKey(stage) ?? step;
  const active = useRef<Driver | null>(null);

  const start = useCallback(() => {
    active.current?.destroy();

    // Filtered here, not at definition time: half the wizard is conditional, and
    // driver.js would otherwise count beats it cannot show.
    const steps = tourForStep(step, stage).filter((s) =>
      document.querySelector(s.element as string)
    );
    if (steps.length === 0) return;

    const instance = driver({
      steps,
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      // The scrim token carries its own alpha in light and not in dark, so the
      // ramp step it is built from is the one thing safe to read here.
      overlayColor: `hsl(${token('--green-950')})`,
      overlayOpacity: 0.45,
      stageRadius: 12,
      stagePadding: 6,
      showProgress: true,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got it',
      onPopoverRender: (popover, { state, driver: tour }) => {
        if (!state.activeStep?.data?.nav) return;
        addSkipButton(popover.footerButtons, () => tour.destroy());
      },
      onDestroyed: () => markSeen(tourKey),
    });
    active.current = instance;
    instance.drive();
  }, [step, stage, tourKey]);

  useEffect(() => {
    if (seenSteps().includes(tourKey)) return undefined;
    // useFocusField is already scrolling and ringing the field jumped to
    if (focusField) {
      markSeen(tourKey);
      return undefined;
    }
    // Marked when shown, not only when destroyed: dismissing with Escape left
    // the key unwritten, so the tour reopened on the next load.
    const timer = setTimeout(() => {
      markSeen(tourKey);
      start();
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [tourKey, focusField, start]);

  useEffect(() => () => active.current?.destroy(), []);

  return start;
}
